import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
} from "react";
import {
  API_EVENT_AUTH_REFRESH,
  apiEventEmitter,
  sendLogs,
} from "../api/client";
import {
  AccountContext,
  LOGIN_EVENT,
  loginEventEmitter,
} from "./AccountContext";
import Log, { LogEventCategory, MetricEvents } from "../api/entities/Log";
import SendLogsRequest from "../api/requests/SendLogsRequest";
import { initializeApp } from "firebase/app";
import {
  getAnalytics,
  setUserId as setUserIdFirebase,
  setUserProperties as setUserPropertiesFirebase,
  logEvent as logEventFirebase,
} from "firebase/analytics";
import ReactPixel from "react-facebook-pixel";
import { MemberStatus, Product, Video } from "@booyaltd/core";
import { v4 as uuidv4 } from "uuid";
import { getAttribution } from "../utils/attribution";
type Timeout = ReturnType<typeof setTimeout>;

// Conversion events (free-trial, purchase) additionally carry the visitor's
// stored marketing attribution (#596, slice of #576) when one exists. Only
// the whitelisted keys are merged; when nothing is stored the payloads are
// untouched.
const conversionAttribution = (): { [key: string]: string } | undefined => {
  const attribution = getAttribution();
  if (!attribution) {
    return undefined;
  }

  const params: { [key: string]: string } = {};
  // All five utm keys the attribution store captures (#576): utm_content and
  // utm_term let marketing compare buttons/versions within one campaign.
  [
    "utm_source",
    "utm_medium",
    "utm_campaign",
    "utm_content",
    "utm_term",
    "campaign",
  ].forEach((key) => {
    const value = attribution.params[key];
    if (value) {
      params[key] = value;
    }
  });

  return Object.keys(params).length > 0 ? params : undefined;
};

export type METRIC_EVENT =
  | {
      type: "login";
      payload: {
        method: string;
        subscriptionStatus: MemberStatus;
      };
    }
  | {
      type: "sign_up";
      payload: { source: string; step?: string; newAccount?: boolean };
    }
  | {
      type: "purchase";
      payload: { paymentId: string; userId: number } & Pick<
        Product,
        | "id"
        | "name"
        | "cost"
        | "memberStatusId"
        | "currency"
        | "stripeReference"
      >;
    }
  | {
      type: "free-trial";
      // #1261: a trial started on the website (computer, no smartphone or
      // tablet) says so, so weekly trial starts split app and website.
      payload: {
        route?: "no-phone";
        device_type?: "phone" | "computer";
        surface?: "my_account" | "sign_up";
      };
    }
  | {
      type: "video_view";
      payload: Pick<Video, "id" | "name" | "tags" | "softLinks">;
    }
  | {
      type: "video_watched";
      payload: Pick<Video, "id" | "name" | "tags" | "softLinks">;
    };

const firebaseConfig = {
  apiKey: "AIzaSyA-dHfnE9HFQ3q2G_MAR_DfDajhk6rzVGY",
  authDomain: "farmflix-1a5bf.firebaseapp.com",
  databaseURL: "https://farmflix-1a5bf.firebaseio.com",
  projectId: "farmflix-1a5bf",
  storageBucket: "farmflix-1a5bf.appspot.com",
  messagingSenderId: "748754689061",
  appId: "1:748754689061:web:972308ebbc34e44f7db3cc",
  measurementId: "G-CBWNS5Y3RN",
};

// Initialize Firebase
export const firebaseApp = initializeApp(firebaseConfig);

const LoggerContext = createContext({
  logEvent: (log: Log) => {},
  logScreenView: (path: string) => {},
  logMetricEvent: (event: METRIC_EVENT, existingEventId?: string): string => "",
});

let lastLogFlush = 0;
let flushTimeout: Timeout | undefined = undefined;
let legacyBrowserReported = false;

const LoggerProvider = (props: any) => {
  const [initialized, setInitialized] = React.useState(false);

  const { userProfile } = useContext(AccountContext);

  const initialise = useCallback(() => {
    if (initialized) {
      return;
    }

    ReactPixel.init("4259638347489125", undefined, {
      // Meta's automatic events stay off: the pixel sends only PageView and
      // StartTrial (#1227).
      autoConfig: false,
      // Pixel verbose logging only outside production builds (#577). Vite
      // statically replaces process.env.NODE_ENV; import.meta is avoided
      // because babel-jest can't parse it.
      debug: process.env.NODE_ENV !== "production",
    });
    setInitialized(true);
  }, [initialized]);

  const flushLogs = useCallback(() => {
    const now = Date.now();
    if (lastLogFlush > now - 30000) {
      return;
    }

    lastLogFlush = now;

    const keys = Object.keys(localStorage).filter((key) =>
      key.includes("ff-log-"),
    );

    if (!keys.length) {
      return;
    }

    const logs = keys
      .map((key) => localStorage.getItem(key))
      .filter((val) => val != null)
      .map((val) => JSON.parse(JSON.stringify(val) as string) as Log);

    if (!logs || logs.length === 0) {
      return;
    }

    sendLogs({ logs } as SendLogsRequest)
      .catch((e) => {
        console.error("Error sending log events");
        console.log(e);
      })
      .finally(() => {
        keys.forEach((key) => localStorage.removeItem(key));
      });
  }, []);

  const logEvent = useCallback(
    async (log: Log) => {
      const timestamp = Date.now();

      const logWithTimestampAndUserId = {
        userId: userProfile?.id,
        category: log.category,
        event: log.event,
        data: log.data,
        timestamp: new Date(),
      } as Log;

      localStorage.setItem(
        `ff-log-${timestamp}-${log.event}`,
        JSON.stringify(logWithTimestampAndUserId),
      );
    },
    [userProfile],
  );

  const logScreenView = useCallback(
    (path: string) => {
      console.log("Logging screen view", path);
      initialise();

      logEventFirebase(getAnalytics(firebaseApp), "page_view", {
        page_path: path,
      });
      ReactPixel.pageView();
    },
    [initialise],
  );

  const logMetricEvent = useCallback(
    (event: METRIC_EVENT, existingEventId?: string): string => {
      console.log("Logging Metric Event", event.type, event.payload);
      initialise();

      const eventId = existingEventId || uuidv4();

      // Marketing attribution (#576) — enrich acquisition-relevant events
      // (login / sign_up / purchase / free-trial) on Firebase, and StartTrial
      // on the FB Pixel, with the whitelisted, flattened params only.
      // Deliberately NOT applied to video_view/video_watched.
      // The pixel sends only PageView and StartTrial (#1227); every other
      // event goes to Firebase and the internal event log only.
      const attribution = conversionAttribution();

      switch (event.type) {
        case "login": {
          const { method, subscriptionStatus } = event.payload;
          logEventFirebase(getAnalytics(firebaseApp), "login", {
            method,
            subscriptionStatus,
            ...(attribution || {}),
          });
          logEvent({
            category: LogEventCategory.ACCOUNT_ACTION,
            event: MetricEvents.LOGIN,
            data: { method, subscriptionStatus, eventId },
            timestamp: new Date(),
            userId: userProfile?.id?.toString(),
          });
          break;
        }

        case "sign_up":
          logEventFirebase(getAnalytics(firebaseApp), "sign_up", {
            source: event.payload.source,
            step: event.payload.step,
            newAccount: event.payload.newAccount,
            ...(attribution || {}),
          });
          logEvent({
            category: LogEventCategory.ACCOUNT_ACTION,
            event: MetricEvents.SIGN_UP,
            data: {
              source: event.payload.source,
              step: event.payload.step,
              newAccount: event.payload.newAccount,
              eventId,
            },
            timestamp: new Date(),
            userId: userProfile?.id?.toString(),
          });
          break;

        case "purchase": {
          const { paymentId, id, name, currency, cost, memberStatusId } =
            event.payload;
          logEventFirebase(getAnalytics(firebaseApp), "purchase", {
            transaction_id: paymentId,
            value: cost,
            currency: (currency || "gbp").toLowerCase(),
            items: [{ item_id: id, item_name: name, price: cost, quantity: 1 }],
            ...(attribution || {}),
          });
          logEvent({
            category: LogEventCategory.ACCOUNT_ACTION,
            event: MetricEvents.PURCHASE,
            data: {
              paymentId,
              id,
              name,
              currency,
              cost,
              memberStatusId,
              eventId,
              ...(attribution || {}),
            },
            timestamp: new Date(),
            userId: userProfile?.id?.toString(),
          });
          break;
        }

        case "free-trial": {
          logEventFirebase(getAnalytics(firebaseApp), "free_trial", {
            ...event.payload,
            ...(attribution || {}),
          });
          // #659: StartTrial pairs with the server's StartTrial (Conversions
          // API) on the same event ID (#1225), so Meta counts the pair once.
          // The custom free_trial pixel event is gone (#1227): no audience or
          // custom conversion used it.
          ReactPixel.fbq(
            "track",
            "StartTrial",
            { ...(attribution || {}) },
            { eventID: eventId },
          );
          logEvent({
            category: LogEventCategory.ACCOUNT_ACTION,
            event: MetricEvents.FREE_TRIAL,
            data: { eventId, ...event.payload, ...(attribution || {}) },
            timestamp: new Date(),
            userId: userProfile?.id?.toString(),
          });
          break;
        }

        case "video_view":
          logEventFirebase(getAnalytics(firebaseApp), "video_view", {
            ...event.payload,
            tags:
              event.payload.tags && Array.isArray(event.payload.tags)
                ? event.payload.tags.join(",")
                : "",
            softLinks:
              event.payload.softLinks && Array.isArray(event.payload.softLinks)
                ? event.payload.softLinks.join(",")
                : "",
          });
          logEvent({
            category: LogEventCategory.PLAYBACK_ACTION,
            event: MetricEvents.VIDEO_VIEW,
            data: { ...event.payload, eventId },
            timestamp: new Date(),
            userId: userProfile?.id?.toString(),
          });
          break;

        case "video_watched":
          logEventFirebase(getAnalytics(firebaseApp), "video_watched", {
            ...event.payload,
            tags:
              event.payload.tags && Array.isArray(event.payload.tags)
                ? event.payload.tags.join(",")
                : "",
            softLinks:
              event.payload.softLinks && Array.isArray(event.payload.softLinks)
                ? event.payload.softLinks.join(",")
                : "",
          });
          logEvent({
            category: LogEventCategory.PLAYBACK_ACTION,
            event: MetricEvents.VIDEO_WATCHED,
            data: { ...event.payload, eventId },
            timestamp: new Date(),
            userId: userProfile?.id?.toString(),
          });
          break;
      }
      return eventId;
    },
    [initialise, logEvent, userProfile],
  );

  const onAuthRefresh = useCallback(() => {
    if (userProfile) {
      logMetricEvent({
        type: "login",
        payload: {
          method: "refresh",
          subscriptionStatus:
            userProfile.memberStatus as unknown as MemberStatus,
        },
      });
    }
  }, [userProfile, logMetricEvent]);

  const onLoginEvent = useCallback(
    (method: string) => {
      if (userProfile) {
        logMetricEvent({
          type: "login",
          payload: {
            method: method,
            subscriptionStatus:
              userProfile.memberStatus as unknown as MemberStatus,
          },
        });
      }
    },
    [userProfile, logMetricEvent],
  );

  useEffect(() => {
    initialise();
  }, [initialise]);

  // Internal-store arm of the #643 legacy-bundle measurement (#649). A
  // browser without script.noModule loaded the plugin-legacy es5 bundle; for
  // signed-in members (the "real customers" the keep/remove decision hinges
  // on) record one event through the existing logEvent -> sendLogs path so
  // uptake is queryable server-side without GA4 creds. Anonymous legacy
  // sessions are covered by the Firebase arm (reportLegacyBrowser at app
  // entry). Gated on userProfile because the /logs endpoint requires auth,
  // and because jsdom reports no script.noModule — the gate (userProfile is
  // undefined in unit tests) keeps this always-on event out of test output.
  useEffect(() => {
    if (legacyBrowserReported || !userProfile) {
      return;
    }
    legacyBrowserReported = true;

    if (!("noModule" in document.createElement("script"))) {
      logEvent({
        category: LogEventCategory.HARDWARE,
        event: MetricEvents.LEGACY_BROWSER,
        data: { userAgent: navigator.userAgent },
        timestamp: new Date(),
        userId: userProfile.id?.toString(),
      });
    }
  }, [userProfile, logEvent]);

  useEffect(() => {
    apiEventEmitter.on(API_EVENT_AUTH_REFRESH, onAuthRefresh);

    return () => {
      apiEventEmitter.off(API_EVENT_AUTH_REFRESH, onAuthRefresh);
    };
  }, [onAuthRefresh]);

  useEffect(() => {
    loginEventEmitter.on(LOGIN_EVENT, onLoginEvent);

    return () => {
      loginEventEmitter.off(LOGIN_EVENT, onLoginEvent);
    };
  }, [onLoginEvent]);

  useEffect(() => {
    if (flushTimeout) {
      console.log("Clearing Flush Interval");
      clearTimeout(flushTimeout);
    }

    if (userProfile) {
      console.log("Registering Flush Interval");
      setUserIdFirebase(getAnalytics(firebaseApp), userProfile.id.toString());
      setUserPropertiesFirebase(getAnalytics(firebaseApp), {
        firstName: userProfile.firstName || "",
        lastName: userProfile.lastName || "",
        email: userProfile.email || "",
        countryCode: userProfile.countryCode || "",
        phoneNumber: userProfile.phoneNumber || "",
        subscriptionStatus: userProfile.memberStatus || "",
      });

      flushTimeout = setInterval(flushLogs, 5000);
    }

    return () => {
      if (flushTimeout) {
        console.log("Clearing Flush Interval");
        clearTimeout(flushTimeout);
      }
    };
  }, [flushLogs, userProfile]);

  return (
    <LoggerContext.Provider value={{ logEvent, logScreenView, logMetricEvent }}>
      {props.children}
    </LoggerContext.Provider>
  );
};

export { LoggerContext, LoggerProvider };
